import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { useLiveTranslatedInput } from "../../hooks/useLiveTranslatedInput";
import { useSendEmail } from "../../api/mutations/useSendEmail";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppContext } from "../../Context/AppContext";
import type { Users } from "../../types/Types";

type ContactFormData = {
  name: string;
  email: string;
  subject: string;
  message: string;
};

const DEFAULT_FORM_VALUES: ContactFormData = {
  name: "",
  email: "",
  subject: "",
  message: "",
};

const SUBJECT_KEYS = [
  "contact.subject.muslimInvest",
  "contact.subject.companyEnquiry",
  "contact.subject.investorEnquiry",
  "contact.subject.mentorAcademy",
  "contact.subject.mentorEnquiry",
  "contact.subject.menteeEnquiry",
  "contact.subject.ventureBuilder",
  "contact.subject.intlMarkets",
  "contact.subject.intlTradeProgram",
  "contact.subject.consultancy",
  "contact.subject.marketAccess",
  "contact.subject.accelerator",
  "contact.subject.recruitment",
  "contact.subject.b2bIntro",
  "contact.subject.events",
  "contact.subject.istianaBoard",
  "contact.subject.complaint",
  "contact.subject.upgradeMembership",
  "contact.subject.cancelMembership",
  "contact.subject.other",
] as const;

function getDisplayName(user: Users | undefined): string {
  const first = user?.user?.firstName;
  const last = user?.user?.lastName;
  return [first, last].filter(Boolean).join(" ").trim() || "";
}

function getInputClassName(hasError: boolean, base = "form-control rounded-3") {
  return `${base} ${hasError ? "is-invalid" : ""}`.trim();
}

function ContactForm() {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState("");

  const {
    bindLiveText,
    retranslateCurrent,
    clearDraft,
    isTranslating: isMessageTranslating,
    messageTargetLang,
  } = useLiveTranslatedInput();

  const messageSnapshotRef = useRef("");

  const schema = useMemo(
    () =>
      yup.object().shape({
        name: yup.string().required(t("contact.validation.nameRequired")),
        email: yup
          .string()
          .email(t("contact.validation.emailInvalid"))
          .required(t("contact.validation.emailRequired")),
        subject: yup.string().required(t("contact.validation.subjectRequired")),
        message: yup
          .string()
          .min(10, t("contact.validation.messageMin"))
          .required(t("contact.validation.messageRequired")),
      }),
    [t]
  );

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>({
    resolver: yupResolver(schema),
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAppContext();

  const onSuccess = useCallback(() => {
    navigate("/contact-confirmation");
  }, [navigate]);

  const onError = useCallback(() => {
    setSubmitError(t("contact.submitError"));
  }, [t]);

  const { mutate: sendMail, isPending } = useSendEmail({
    onSuccess,
    onError,
  });

  const onSubmit: SubmitHandler<ContactFormData> = useCallback(
    (data) => {
      setSubmitError("");

      const userName = getDisplayName(user);
      const userEmail = user?.user?.email ?? "";

      let emailBody = data.message;
      if (userName || userEmail) {
        emailBody += `\n\n---\n\n${t("contact.emailClosing")}`;
        if (userName) {
          emailBody += `\n${userName}`;
        }
        if (userEmail) {
          emailBody += `\n${userEmail}`;
        }
      }

      sendMail({
        email: "",
        subject: data.subject,
        body: emailBody,
        name: data.name,
      });
    },
    [sendMail, user, t]
  );

  useEffect(() => {
    const fromQuery: Partial<ContactFormData> = {
      name: searchParams.get("name")?.trim() || undefined,
      email: searchParams.get("email")?.trim() || undefined,
      subject: searchParams.get("subject")?.trim() || undefined,
      message: searchParams.get("message")?.trim() || undefined,
    };

    if (user) {
      reset({
        ...DEFAULT_FORM_VALUES,
        name: fromQuery.name || getDisplayName(user),
        email: fromQuery.email || (user.user?.email ?? ""),
        subject: fromQuery.subject || "",
        message: fromQuery.message || "",
      });
      messageSnapshotRef.current = fromQuery.message || "";
      clearDraft();
      return;
    }

    if (fromQuery.name || fromQuery.email || fromQuery.subject || fromQuery.message) {
      reset({
        ...DEFAULT_FORM_VALUES,
        name: fromQuery.name || "",
        email: fromQuery.email || "",
        subject: fromQuery.subject || "",
        message: fromQuery.message || "",
      });
      messageSnapshotRef.current = fromQuery.message || "";
    }
  }, [user, reset, clearDraft, searchParams]);

  useEffect(() => {
    retranslateCurrent(
      () => messageSnapshotRef.current || getValues("message") || "",
      (s) =>
        setValue("message", s, { shouldValidate: true, shouldDirty: true })
    );
  }, [messageTargetLang, retranslateCurrent, getValues, setValue]);

  return (
    <div className="card shadow-lg border-0 rounded-2">
      <div className="card-header gradient-bg text-white d-flex justify-content-between align-items-center">
        <h4 className="mb-0">{t("contact.title")}</h4>
      </div>
      <div className="p-4">
        {submitError && (
          <div className="alert alert-danger rounded-3">{submitError}</div>
        )}

        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          aria-label={t("contact.formAria")}
        >
          <div className="d-flex flex-column flex-md-row gap-3 mb-4">
            <div className="flex-grow-1">
              <label htmlFor="name" className="form-label fw-semibold">
                {t("contact.label.name")}
              </label>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <input
                    readOnly
                    type="text"
                    id="name"
                    className={getInputClassName(!!errors.name)}
                    placeholder={t("contact.placeholder.name")}
                    {...field}
                  />
                )}
              />
              {errors.name && (
                <div className="invalid-feedback">{errors.name.message}</div>
              )}
            </div>

            <div className="flex-grow-1">
              <label htmlFor="email" className="form-label fw-semibold">
                {t("contact.label.email")}
              </label>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <input
                    readOnly
                    type="email"
                    id="email"
                    className={getInputClassName(!!errors.email)}
                    placeholder={t("contact.placeholder.email")}
                    {...field}
                  />
                )}
              />
              {errors.email && (
                <div className="invalid-feedback">{errors.email.message}</div>
              )}
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="subject" className="form-label fw-semibold">
              {t("contact.label.subject")}
            </label>
            <Controller
              name="subject"
              control={control}
              render={({ field }) => (
                <select
                  id="subject"
                  className={getInputClassName(
                    !!errors.subject,
                    "form-select rounded-3"
                  )}
                  {...field}
                >
                  <option value="">{t("contact.selectSubject")}</option>
                  {SUBJECT_KEYS.map((key) => {
                    const label = t(key);
                    return (
                      <option key={key} value={label}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              )}
            />
            {errors.subject && (
              <div className="invalid-feedback">{errors.subject.message}</div>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="message" className="form-label fw-semibold">
              {t("contact.label.message")}
            </label>
            <Controller
              name="message"
              control={control}
              render={({ field }) => {
                messageSnapshotRef.current = field.value;
                return (
                <textarea
                  id="message"
                  lang={messageTargetLang}
                  className={getInputClassName(!!errors.message)}
                  rows={5}
                  placeholder={t("contact.placeholder.message")}
                  {...field}
                  onChange={(e) => {
                    messageSnapshotRef.current = e.target.value;
                    field.onChange(e);
                    bindLiveText(e.target.value, (s) =>
                      setValue("message", s, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    );
                  }}
                />
                );
              }}
            />
            {isMessageTranslating && (
              <div className="form-text" aria-live="polite">
                {t("contact.messageTranslating")}
              </div>
            )}
            {errors.message && (
              <div className="invalid-feedback d-block">
                {errors.message.message}
              </div>
            )}
          </div>
          <button
            type="submit"
            className="btn btn-primary w-100"
            disabled={isPending}
          >
            {isPending ? t("contact.sending") : t("contact.send")}
          </button>
        </form>
      </div>
    </div>
  );
}

ContactForm.displayName = "ContactForm";

export default memo(ContactForm);
